import React, { useState, useEffect, useCallback } from 'react';
import './Furdle.css';
import { SiteNav } from './App';
import { ALL_VALID_WORDS } from './words';

// Word lists organized by length (for selecting target words)
const WORDS = {
    4: ['ABLE', 'ARCH', 'ATOM', 'BEND', 'BIRD', 'BLOW', 'BULK', 'CAKE', 'CALM', 'COIL', 'CORD', 'DARK', 'DEAL', 'DEEP', 'DIRE', 'DRAW', 'EASY', 'ECHO', 'EDGE', 'EPIC'],
    5: ['ABOUT', 'ABOVE', 'ABUSE', 'ACTOR', 'ACUTE', 'ADMIT', 'ADOPT', 'ADULT', 'AFTER', 'AGAIN', 'AGENT', 'AGREE', 'AHEAD', 'ALARM', 'ALBUM', 'ALIEN', 'ALIGN', 'ALIVE', 'ALLOW', 'ALONE', 'ALONG', 'ANGER', 'ANGLE', 'ANGRY', 'APART'],
    6: ['ABSORB', 'ACCENT', 'ACCEPT', 'ACCESS', 'ACROSS', 'ACTING', 'ACTION', 'ACTIVE', 'ACTUAL', 'ADJUST', 'ADMIRE', 'AFFORD', 'AFRAID', 'AGENCY', 'AGENDA', 'ALMOST', 'ALWAYS', 'AMOUNT', 'ANIMAL', 'ANNUAL', 'ANSWER', 'APPEAR', 'AROUND', 'ARRIVE', 'ASPECT'],
    7: ['ABILITY', 'ABSENCE', 'ACADEMY', 'ACCOUNT', 'ACCUSED', 'ACHIEVE', 'ACQUIRE', 'ADDRESS', 'ADVANCE', 'ADVERSE', 'ADVISED', 'AFFAIRS', 'ALREADY', 'ANCIENT', 'ANOTHER', 'ANXIETY', 'ANXIOUS', 'ANYBODY', 'ANYMORE', 'ARRANGE', 'ARTICLE', 'ASSAULT', 'ATTEMPT', 'ATTRACT', 'BALANCE'],
    8: ['ABSOLUTE', 'ABSTRACT', 'ACADEMIC', 'ACCEPTED', 'ACCIDENT', 'ACCURATE', 'ACTUALLY', 'ADDITION', 'ADEQUATE', 'ADJACENT', 'ADVANCED', 'ADVOCATE', 'AFFECTED', 'ALTHOUGH', 'AMERICAN', 'ANALYSIS', 'ANNOUNCE', 'ANYTHING', 'ANYWHERE', 'BASEBALL']
};

const MAX_GUESSES = 6;
const KEYBOARD_ROWS = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['Enter', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'Backspace']
];

const Furdle = () => {

    const [targetWord, setTargetWord] = useState('');
    const [targetLength, setTargetLength] = useState(5);
    const [currentGuess, setCurrentGuess] = useState('');
    const [currentRow, setCurrentRow] = useState(0);
    const [guesses, setGuesses] = useState([]);
    const [gameOver, setGameOver] = useState(false);
    const [keyboardState, setKeyboardState] = useState({});
    const [message, setMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', message: '' });
    const [selectedLength, setSelectedLength] = useState('random');

    const pickTargetWord = useCallback((length) => {
        const wordList = WORDS[length];
        return wordList[Math.floor(Math.random() * wordList.length)];
    }, []);

    const initGame = useCallback(() => {
        let length;
        if (selectedLength === 'random') {
            const lengths = [4, 5, 6, 7, 8];
            length = lengths[Math.floor(Math.random() * lengths.length)];
        } else {
            length = parseInt(selectedLength);
        }

        setTargetLength(length);
        setTargetWord(pickTargetWord(length));
        setCurrentGuess('');
        setCurrentRow(0);
        setGuesses([]);
        setGameOver(false);
        setKeyboardState({});
        setShowModal(false);
    }, [selectedLength, pickTargetWord]);

    useEffect(() => {
        initGame();
    }, [initGame]);

    const showTemporaryMessage = useCallback((text) => {
        setMessage(text);
        setTimeout(() => setMessage(''), 2000);
    }, []);

    const scoreGuess = useCallback((guess, target) => {
        const statuses = Array(guess.length).fill('absent');
        const targetLetters = target.split('');
        const guessLetters = guess.split('');

        // First pass: mark correct letters
        for (let i = 0; i < guessLetters.length; i++) {
            if (guessLetters[i] === targetLetters[i]) {
                statuses[i] = 'correct';
                targetLetters[i] = null;
            }
        }

        // Second pass: mark present letters
        for (let i = 0; i < guessLetters.length; i++) {
            if (statuses[i] === 'correct') continue;

            const letterIndex = targetLetters.indexOf(guessLetters[i]);
            if (letterIndex !== -1) {
                statuses[i] = 'present';
                targetLetters[letterIndex] = null;
            }
        }

        return statuses;
    }, []);

    const updateKeyboard = useCallback((guess, statuses) => {
        const priority = { correct: 3, present: 2, absent: 1 };
        setKeyboardState((currentKeyboardState) => {
            const newKeyboardState = { ...currentKeyboardState };

            for (let i = 0; i < guess.length; i++) {
                const letter = guess[i].toLowerCase();
                const status = statuses[i];

                const currentStatus = newKeyboardState[letter];
                if (!currentStatus || priority[status] > priority[currentStatus]) {
                    newKeyboardState[letter] = status;
                }
            }

            return newKeyboardState;
        });
    }, []);

    const submitGuess = useCallback(() => {
        if (currentGuess.length !== targetLength) {
            showTemporaryMessage(`Need ${targetLength} letters`);
            return;
        }

        if (!ALL_VALID_WORDS.has(currentGuess)) {
            showTemporaryMessage('Not in word list');
            return;
        }

        const statuses = scoreGuess(currentGuess, targetWord);
        setGuesses([...guesses, { word: currentGuess, statuses }]);
        updateKeyboard(currentGuess, statuses);

        if (currentGuess === targetWord) {
            setGameOver(true);
            setTimeout(() => {
                setModalContent({
                    title: '🎉 You Win! 🎉',
                    message: `You guessed the word in ${currentRow + 1} ${currentRow + 1 === 1 ? 'try' : 'tries'}!`,
                    word: targetWord
                });
                setShowModal(true);
            }, 1500);
            return;
        }

        if (currentRow + 1 >= MAX_GUESSES) {
            setGameOver(true);
            setTimeout(() => {
                setModalContent({
                    title: 'Game Over',
                    message: 'The word was:',
                    word: targetWord
                });
                setShowModal(true);
            }, 1500);
            return;
        }

        setCurrentRow(currentRow + 1);
        setCurrentGuess('');
    }, [
        currentGuess,
        currentRow,
        guesses,
        scoreGuess,
        showTemporaryMessage,
        targetLength,
        targetWord,
        updateKeyboard
    ]);

    const handleKey = useCallback((key) => {
        if (gameOver) return;

        if (key === 'Enter') {
            submitGuess();
        } else if (key === 'Backspace') {
            setCurrentGuess(prev => prev.slice(0, -1));
        } else if (/^[a-z]$/.test(key)) {
            if (currentGuess.length < targetLength) {
                setCurrentGuess(prev => prev + key.toUpperCase());
            }
        }
    }, [gameOver, currentGuess, targetLength, submitGuess]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                handleKey('Enter');
            } else if (e.key === 'Backspace') {
                handleKey('Backspace');
            } else if (/^[a-zA-Z]$/.test(e.key)) {
                handleKey(e.key.toLowerCase());
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKey]);

    const renderGrid = () => {
        const rows = [];
        for (let i = 0; i < MAX_GUESSES; i++) {
            const tiles = [];
            for (let j = 0; j < targetLength; j++) {
                let letter = '';
                let className = 'tile';

                if (i < guesses.length) {
                    letter = guesses[i].word[j];
                    className += ` ${guesses[i].statuses[j]}`;
                } else if (i === currentRow) {
                    letter = currentGuess[j] || '';
                    if (letter) className += ' filled';
                }

                tiles.push(
                    <div key={j} className={className}>
                        {letter}
                    </div>
                );
            }
            rows.push(
                <div key={i} className="row" style={{ gridTemplateColumns: `repeat(${targetLength}, 1fr)` }}>
                    {tiles}
                </div>
            );
        }
        return rows;
    };

    if (!ALL_VALID_WORDS) {
        return (
            <>
                <SiteNav />
                <main className="furdle-container loading">Loading word dictionary...</main>
            </>
        );
    }

    return (
        <>
        <SiteNav />
        <main className="furdle-container">
            <header className="furdle-header">
                <h1>FURDLE</h1>
            </header>

            <div className="controls">
                <select
                    id="lengthSelect"
                    value={selectedLength}
                    onChange={(e) => setSelectedLength(e.target.value)}
                >
                    <option value="random">Random Length</option>
                    <option value="4">4 Letters</option>
                    <option value="5">5 Letters</option>
                    <option value="6">6 Letters</option>
                    <option value="7">7 Letters</option>
                    <option value="8">8 Letters</option>
                </select>
                <button onClick={initGame}>New Game</button>
            </div>

            <div className="rules">
                <h3>How to Play</h3>
                <p>Guess the word in 6 tries. Each guess must match the target word length.</p>
                <ul>
                    <li>After each guess, tiles show how close your guess was:</li>
                </ul>
                <div className="examples">
                    <div className="example-tile correct">A</div>
                    <span>= correct letter, correct position</span>
                </div>
                <div className="examples">
                    <div className="example-tile present">E</div>
                    <span>= correct letter, wrong position</span>
                </div>
                <div className="examples">
                    <div className="example-tile absent">X</div>
                    <span>= letter not in word</span>
                </div>
            </div>

            <div className="game-board">
                {renderGrid()}
            </div>

            <div className="keyboard">
                {KEYBOARD_ROWS.map((row, i) => (
                    <div key={i} className="keyboard-row">
                        {row.map((key) => (
                            <button
                                key={key}
                                className={`key ${key.length > 1 ? 'wide' : ''} ${keyboardState[key] || ''}`}
                                onClick={() => handleKey(key)}
                            >
                                {key === 'Backspace' ? '⌫' : key.toUpperCase()}
                            </button>
                        ))}
                    </div>
                ))}
            </div>

            {message && <div className="message">{message}</div>}

            {showModal && (
                <>
                    <div className="overlay" onClick={() => setShowModal(false)}></div>
                    <div className="modal">
                        <h2>{modalContent.title}</h2>
                        <p>{modalContent.message}</p>
                        <p className="target-word">{modalContent.word}</p>
                    </div>
                </>
            )}
        </main>
        </>
    );
};

export default Furdle;
